import React, { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Switch,
} from 'antd';
import PhoneInput from 'antd-phone-input';
import { useTranslation } from 'react-i18next';

import { Company } from 'models/Company';
import { Country } from 'models/Country';
import { ItemInterface } from 'models/ItemInterface';
import { Role } from 'models/Role';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import appI18n from '../../../../config/i18n';
import { USER_TYPES } from '../SuperUsers';

interface NewRoleModalProps {
  isOpen: boolean;
  handleModalCancel: () => void;
  submit: (
    name: string,
    lastname: string,
    email: string,
    dialCode: string | null,
    phone: string | null,
    roleIds: number[],
    companyId: number,
    userType: 'SUPER_ADMIN' | 'ADMIN',
    restrictedToCountries: boolean,
    allowedCountries: string[]
  ) => void;
  roles: Role[];
  defaultCountryCode: string;
  companies: Company[];
  countries: Country[];
  getDialCode: (countryCode: string) => string;
}

function NewUserModal({
  isOpen,
  handleModalCancel,
  submit,
  roles,
  defaultCountryCode,
  companies,
  countries,
  getDialCode,
}: NewRoleModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company>();
  const [selectedUserType, setSelectedUserType] = useState<string | undefined>(
    undefined
  );
  const [restrictedToCountries, setRestrictedToCountries] = useState(false);
  const { t } = useTranslation(undefined, { i18n: appI18n });

  useEffect(() => {
    form.resetFields();
    setSelectedCompany(undefined);
    setSelectedUserType(undefined);
    setRestrictedToCountries(false);

    if (isOpen) {
      form.setFieldValue('phone', {
        ...form.getFieldValue('phone'),
        countryCode: parseInt(defaultCountryCode),
      });
    }
  }, [isOpen]);

  const onFinish = async (values: any) => {
    setLoading(true);

    await submit(
      values.name,
      values.lastname,
      values.email,
      values.phone.phoneNumber === null ? null : `+${values.phone.countryCode}`,
      values.phone.phoneNumber === null
        ? null
        : `${values.phone.areaCode}${values.phone.phoneNumber}`,
      values.roleIds || [],
      values.company,
      values.userType,
      restrictedToCountries,
      restrictedToCountries ? values.allowedCountries || [] : []
    );

    setLoading(false);
  };

  const getRoles = (): ItemInterface[] => {
    return roles
      .filter((record) => {
        return record.companyId === selectedCompany?.id;
      })
      .map((record) => {
        return {
          value: record.id,
          label: record.name,
        };
      });
  };

  const getCompanies = (): ItemInterface[] => {
    return companies.map((record) => {
      return {
        value: record.id,
        label: record.name,
      };
    });
  };

  const getUserTypeOptions = (): ItemInterface[] => {
    return [
      {
        value: USER_TYPES.SUPER_ADMIN,
        label: t(
          'scenes.settings.super_users.scene.table.user_type.super_admin'
        ),
      },
      {
        value: USER_TYPES.ADMIN,
        label: t('scenes.settings.super_users.scene.table.user_type.admin'),
      },
    ];
  };

  const getCountryOptions = () => {
    return countries.map((record) => ({
      label: record.name,
      value: record.code,
    }));
  };

  const handleChangeCompany = (newValue: number) => {
    form.setFieldValue('roleIds', []);

    let dialCode = defaultCountryCode;

    if (newValue) {
      const company = companies.find((record) => {
        return record.id === newValue;
      });

      if (company) {
        dialCode = getDialCode(company.countryCode);
        setSelectedCompany(company);
      }
    } else {
      setSelectedCompany(undefined);
    }

    if (!form.getFieldValue('phone').phoneNumber) {
      form.setFieldValue('phone', {
        ...form.getFieldValue('phone'),
        countryCode: parseInt(dialCode),
      });
    }
  };

  const handleChangeUserType = (newValue: string) => {
    setSelectedUserType(newValue);
    setRestrictedToCountries(false);
    form.setFieldValue('allowedCountries', []);
  };

  const handleChangeRestrictedToCountries = (checked: boolean) => {
    setRestrictedToCountries(checked);
    if (!checked) {
      form.setFieldValue('allowedCountries', []);
    }
  };

  return (
    <>
      <Modal
        title={t('scenes.settings.super_users.components.new_user_modal.title')}
        open={isOpen}
        onCancel={handleModalCancel}
        maskClosable={false}
        destroyOnClose
        footer={null}
        width={480}
      >
        <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

        <Row gutter={24} style={{ marginBottom: '18px' }}>
          <Col xs={24}>
            <Alert
              message={t(
                'scenes.settings.super_users.components.new_user_modal.alert_info'
              )}
              type="info"
              showIcon
            />
          </Col>
        </Row>

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="userType"
                label={t(
                  'scenes.settings.super_users.components.new_user_modal.labels.user_type'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.super_users.components.new_user_modal.validations.user_type.required'
                    ),
                  },
                ]}
              >
                <Select
                  style={{ width: '100%' }}
                  options={getUserTypeOptions()}
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.super_users.components.new_user_modal.placeholders.user_type'
                  )}
                  onChange={handleChangeUserType}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="company"
                label={t(
                  'scenes.settings.super_users.components.new_user_modal.labels.company'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.super_users.components.new_user_modal.validations.company.required'
                    ),
                  },
                ]}
              >
                <Select
                  style={{ width: '100%' }}
                  options={getCompanies()}
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.super_users.components.new_user_modal.placeholders.company'
                  )}
                  showSearch
                  filterOption={(input, option) => {
                    return (
                      FunctionsHelper.normalizeText(option?.label || '')
                        .toLowerCase()
                        .indexOf(
                          FunctionsHelper.normalizeText(input.toLowerCase())
                        ) >= 0
                    );
                  }}
                  onChange={(v) => {
                    handleChangeCompany(v);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="email"
                label={t(
                  'scenes.settings.super_users.components.new_user_modal.labels.email'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.super_users.components.new_user_modal.validations.email.required'
                    ),
                  },
                  {
                    type: 'email',
                    message: t(
                      'scenes.settings.super_users.components.new_user_modal.validations.email.invalid'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.super_users.components.new_user_modal.placeholders.email'
                  )}
                  addonAfter={'@'}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="name"
                label={t(
                  'scenes.settings.super_users.components.new_user_modal.labels.name'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.super_users.components.new_user_modal.validations.name.required'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.super_users.components.new_user_modal.placeholders.name'
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="lastname"
                label={t(
                  'scenes.settings.super_users.components.new_user_modal.labels.lastname'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.super_users.components.new_user_modal.validations.lastname.required'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.super_users.components.new_user_modal.placeholders.lastname'
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="phone"
                label={t(
                  'scenes.settings.super_users.components.new_user_modal.labels.phone'
                )}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      if (value.valid()) {
                        return Promise.resolve();
                      }

                      if (!value.valid() && value.phoneNumber === null) {
                        return Promise.resolve();
                      }

                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.settings.super_users.components.new_user_modal.validations.phone.invalid'
                          )
                        )
                      );
                    },
                  },
                ]}
              >
                <PhoneInput
                  enableSearch
                  searchNotFound={t(
                    'scenes.settings.super_users.components.new_user_modal.phone.search_not_found'
                  )}
                  searchPlaceholder={t(
                    'scenes.settings.super_users.components.new_user_modal.phone.search_placeholder'
                  )}
                  allowClear
                  placeholder={t(
                    'scenes.settings.super_users.components.new_user_modal.placeholders.phone'
                  )}
                  disabled={loading}
                  onChange={(v) => {
                    if (!v.countryCode && !v.phoneNumber && !v.areaCode) {
                      form.setFieldValue('phone', {
                        ...form.getFieldValue('phone'),
                        countryCode: selectedCompany
                          ? parseInt(getDialCode(selectedCompany.countryCode))
                          : parseInt(defaultCountryCode),
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="roleIds"
                label={t(
                  'scenes.settings.super_users.components.new_user_modal.labels.roles'
                )}
              >
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  options={getRoles()}
                  disabled={loading || !selectedCompany}
                  placeholder={t(
                    'scenes.settings.super_users.components.new_user_modal.placeholders.roles'
                  )}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          {selectedUserType === USER_TYPES.ADMIN && (
            <>
              <Row gutter={24}>
                <Col xs={24}>
                  <Form.Item
                    label={t(
                      'scenes.settings.super_users.components.new_user_modal.labels.restricted_to_countries'
                    )}
                  >
                    <Switch
                      checked={restrictedToCountries}
                      onChange={handleChangeRestrictedToCountries}
                      disabled={loading}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {restrictedToCountries && (
                <Row gutter={24}>
                  <Col xs={24}>
                    <Form.Item
                      name="allowedCountries"
                      label={t(
                        'scenes.settings.super_users.components.new_user_modal.labels.allowed_countries'
                      )}
                      rules={[
                        {
                          validator: (_, value) => {
                            if (
                              restrictedToCountries &&
                              (!value || value.length === 0)
                            ) {
                              return Promise.reject(
                                new Error(
                                  t(
                                    'scenes.settings.super_users.components.new_user_modal.validations.allowed_countries.min_one'
                                  )
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Checkbox.Group
                        options={getCountryOptions()}
                        disabled={loading}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </>
          )}

          <Row gutter={24}>
            <Col xs={24}>
              <Button
                onClick={() => {
                  form.submit();
                }}
                loading={loading}
                type="primary"
                block
              >
                {t(
                  'scenes.settings.super_users.components.new_user_modal.buttons.save_changes'
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default NewUserModal;
